import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { LookupLists, INVOICE_TO, CustomerDetails, State, Branch, Customer } from '@pos-app/data';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ApiService } from '@pos-app/core-ui';
import { getCombinedBranches } from '@pos-app/core-ui/lib/utils/utils-orders-combine-branches';
import { CoreUiModule } from '../../../../../../../../libs/core-ui/src/lib/core-ui.module';
import { NgIf, NgFor } from '@angular/common';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';

@Component({
    selector: 'app-customers-ship-to-form',
    templateUrl: './customers-ship-to-form.component.html',
    styleUrls: ['./customers-ship-to-form.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, CoreUiModule, NgIf, NgSelectComponent, NgFor, NgOptionComponent]
})
export class CustomersShipToFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() soldToCustomer: CustomerDetails;
  @Input() selectedCustomer: CustomerDetails;
  @Input() lookupLists: LookupLists;
  @Output() formChanged = new EventEmitter<any>();
  @Output() formIsValid = new EventEmitter<boolean>();
  @Output() formIsDirty = new EventEmitter<boolean>();
  @Output() submitByEnter = new EventEmitter<void>();
  @Output() selectedCountry = new EventEmitter<string>();
  @Input() disabledDefaultBillto: boolean;
  @Input() allCombinedBranchesList: Branch[] = [];

  public stateList: State[];
  public soldToCustomerData = {
    name: '',
    number: '',
  };
  @Input() set filteredStateList(value) {
    this.stateList = value;
  }

  customer: Customer;
  public customerShipToForm: UntypedFormGroup;
  public isAccountCustomer = false;
  public lookupLists$: Observable<LookupLists>;
  public invoiceToList = INVOICE_TO;
  private unSubscribe$ = new Subject<void>();

  constructor(private formBuilder: UntypedFormBuilder, private apiService: ApiService) {
    this.customerShipToForm = this.formBuilder.group({
      customerNumber: [''],
      branch: [''],
      name: ['', Validators.required],
      // TODO email and phone validation
      email: ['', [Validators.email]],
      phone: ['', [Validators.pattern('^[0-9 ]{10,}$')]],
      phoneType: [''],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      suburb: [''],
      state: [''],
      postCode: [''],
      country: ['AU'],
      soldTo: [''],
      invoiceTo: ['P'],
      delInstructions1: [''],
      delInstructions2: [''],
      zone: [''],
      carrier: [''],
      route: [''],
      freightHandlingCode: [''],
      defaultBillTo: [''],
      despatchBranch: '',
      corpDespatchBranchTF: false,
    });
  }

  ngOnInit() {
    if (this.allCombinedBranchesList && this.allCombinedBranchesList.length > 0) {
      this.allCombinedBranchesList = this.allCombinedBranchesList.map((branch: any) => {
        return {
          BranchCode: branch?.branchCode?.trim() || branch?.BranchCode,
          BranchDescription: branch?.branchName || branch?.BranchDescription,
        };
      });
      if (this.allCombinedBranchesList?.length === 1) {
        this.customerShipToForm.patchValue({ despatchBranch: this.allCombinedBranchesList[0]?.BranchCode }, { emitEvent: false });
      }
    } else {
      this.apiService.fetchCustomerDetails({ customerNumber: this.soldToCustomer?.customerNumber }).subscribe((customerDetails) => {
        if (customerDetails?.SearchResults?.length > 0) {
          this.customer = customerDetails.SearchResults[0];
          this.allCombinedBranchesList = getCombinedBranches(this.customer?.allowedBranches, this.customer?.allowedNonCorps);
          if (this.allCombinedBranchesList?.length === 1) {
            this.customerShipToForm.patchValue({ despatchBranch: this.allCombinedBranchesList[0]?.BranchCode }, { emitEvent: false });
          }
        }
      });
    }
    setTimeout(() => {
      this.initValues();
    }, 500); 
  }
  initValues() {
    this.customerShipToForm.valueChanges.pipe(takeUntil(this.unSubscribe$), distinctUntilChanged()).subscribe(() => {
      this.formChanged.emit(this.customerShipToForm.getRawValue());
      this.formIsValid.emit(this.customerShipToForm.valid);
      this.formIsDirty.emit(this.customerShipToForm.dirty);
    });
    this.customerShipToForm.controls.country.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value) => {
      this.selectedCountry.emit(value);
      this.customerShipToForm.controls.state.patchValue('');
    });
    this.selectedCountry.emit(this.customerShipToForm.controls.country.value);
    // fetch customer details to get  despatch location as combined branch allowed branch
    if (this.soldToCustomer && !this.allCombinedBranchesList) {
      this.apiService.fetchCustomerDetails({ customerNumber: this.soldToCustomer?.customerNumber }).subscribe((customerDetails) => {
        if (customerDetails?.SearchResults?.length > 0) {
          this.customer = customerDetails.SearchResults[0];
          this.allCombinedBranchesList = getCombinedBranches(this.customer?.allowedBranches, this.customer?.allowedNonCorps);
        }
      });
    }
    this.customerShipToForm.controls.despatchBranch.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value) => {
      if (value) {
        //find selected value from allowed branches or shipTos
        if (this.customer?.allowedBranches.some((branch) => branch?.branchCode.trim() === value)) {
          this.customerShipToForm?.patchValue({ corpDespatchBranchTF: true });
        } else {
          this.customerShipToForm?.patchValue({ corpDespatchBranchTF: false });
        }
      } else {
        this.customerShipToForm?.patchValue({ corpDespatchBranchTF: '' });
      }
    });
    this.customerShipToForm.controls.defaultBillTo.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value) => {
      if (!value) {
        this.customerShipToForm?.patchValue({ corpDespatchBranchTF: '' });
      }
    });
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // new shipTo customer, soldTo is the retail customer number
    if (changes.soldToCustomer && changes.soldToCustomer.currentValue && this.customerShipToForm) {
      this.customerShipToForm.patchValue({
        soldTo: this.soldToCustomer?.customerNumber,
        defaultBillTo: this.soldToCustomer?.customerNumber,
      });
      this.soldToCustomerData.name = this.soldToCustomer?.customerName;
      this.soldToCustomerData.number = this.soldToCustomer?.customerNumber;
    }

    // existing shipTo customer
    if (changes.selectedCustomer && changes.selectedCustomer.currentValue && this.customerShipToForm) {
      this.soldToCustomerData.name = this.selectedCustomer.billToName;
      this.soldToCustomerData.number = this.selectedCustomer.billToNumber;
      this.customerShipToForm.patchValue(
        {
          customerNumber: this.selectedCustomer.customerNumber,
          branch: this.selectedCustomer.branchCode,
          name: this.selectedCustomer.customerName,
          email: this.selectedCustomer.email,
          phone: this.selectedCustomer.phone,
          soldTo: this.selectedCustomer.customerNumber,
          addressLine1: this.selectedCustomer.addressLine1,
          addressLine2: this.selectedCustomer.addressLine2,
          suburb: this.selectedCustomer.suburb,
          state: this.selectedCustomer.stateCode,
          postCode: this.selectedCustomer.postCode,
          country: this.selectedCustomer.countryCode,
          invoiceTo: this.selectedCustomer.invoiceTo,
          delInstructions1: this.selectedCustomer.delInstructions1,
          delInstructions2: this.selectedCustomer.delInstructions2,
          zone: this.selectedCustomer.zone,
          carrier: this.selectedCustomer.carrier,
          route: this.selectedCustomer.routeCode,
          freightHandlingCode: this.selectedCustomer.freightHandlingCode,
          defaultBillTo:
            this.selectedCustomer?.billToNumber === this.selectedCustomer?.customerNumber
              ? this.soldToCustomer?.customerNumber ?? '0'
              : this.selectedCustomer.billToNumber,
          despatchBranch: this.selectedCustomer?.despatchLocation,
        },
        { emitEvent: false, onlySelf: true }
      );
      if (String(this.selectedCustomer?.billToNumber) === String(this.selectedCustomer?.customerNumber)) {
        this.customerShipToForm?.controls.defaultBillTo?.disable();
      }
    }
  }

  pressEnter() {
    this.submitByEnter.emit();
  }
}

